import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatCard } from "@/components/shared/StatCard";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { RecordTable } from "@/components/shared/RecordTable";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as Icons from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { ActivityList } from "@/components/shared/ActivityList";
import { inr, text, list, fmtDate, toDate } from "@/lib/format";
import { AttentionBoard } from "@/components/shared/AttentionBoard";
import { useNotifications } from "@/context/NotificationsContext";
import { healthOf } from "@/lib/health";

const KPI_PATH = { customers: "users", requests: "requests", floated: "requests", projects: "projects", professionals: "professionals", approvals: "professionals", care: "disputes", payments: "payments" };

const DAY = 86400000;
const isNewSince = (v, ms) => { const d = toDate(v); return d ? (Date.now() - d.getTime()) <= ms : false; };
const groupTop = (rows, fn, n = 6) => {
  const m = {};
  rows.forEach((r) => { const k = fn(r); if (k) m[k] = (m[k] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, n);
};
const proIds = (p) => (Array.isArray(p.professional_ids) && p.professional_ids.length ? p.professional_ids : [p.professional_id].filter(Boolean));

export default function Dashboard() {
  const nav = useNavigate();
  const users = useCollection("users");
  const requests = useCollection("requests");
  const projects = useCollection("projects");
  const pros = useCollection("professionals");
  const payments = useCollection("payments");
  const leads = useCollection("lead_distributions");
  const disputes = useCollection("disputes");
  const activity = useCollection("activity_logs");
  const [active, setActive] = useState(null);

  const customers = useMemo(() => users.filter((u) => (u.role || "customer") === "customer"), [users]);
  const nameById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name || u.full_name])), [users]);
  const proById = useMemo(() => Object.fromEntries(pros.map((p) => [p.id, p])), [pros]);
  const reqByCust = useMemo(() => { const m = {}; requests.forEach((r) => { (m[r.customer_id] ||= []).push(r); }); return m; }, [requests]);
  const projByCust = useMemo(() => { const m = {}; projects.forEach((p) => { (m[p.customer_id] ||= []).push(p); }); return m; }, [projects]);
  const leadCountByReq = useMemo(() => { const m = {}; leads.forEach((l) => { m[l.request_id] = (m[l.request_id] || 0) + 1; }); return m; }, [leads]);
  const custCity = (u) => (reqByCust[u.id]?.[0]?.city) || u.city || "—";

  const activePros = pros.filter((p) => p.status === "active").length;
  const submittedPros = pros.filter((p) => p.onboarding_status === "submitted");
  const activeProjects = projects.filter((p) => p.status === "active");
  const promiseProjects = projects.filter((p) => p.brugo_promise);
  const zeroReads = requests.filter((r) => !leadCountByReq[r.id]);
  const respondedLeads = leads.filter((l) => l.status === "responded" || l.responded_at).length;
  const expiredLeads = leads.filter((l) => l.status === "expired").length;
  const paymentsTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const openDisputes = disputes.filter((d) => (d.status || "open") === "open").length;

  const recent = useMemo(() => [...activity].sort((a, b) => (toDate(b.server_ts || b.created_at)?.getTime() || 0) - (toDate(a.server_ts || a.created_at)?.getTime() || 0)).slice(0, 10), [activity]);

  // ---- KPI card definitions (value + summary tiles + breakdowns + real record table) ----
  const cards = useMemo(() => [
    {
      key: "customers", label: "Customers", icon: "Users", accent: "primary", value: String(customers.length),
      tiles: [
        ["Total", customers.length], ["New today", customers.filter((u) => isNewSince(u.created_at, DAY)).length],
        ["New this week", customers.filter((u) => isNewSince(u.created_at, 7 * DAY)).length], ["Active", customers.filter((u) => (u.status || "active") === "active").length],
        ["With requests", customers.filter((u) => reqByCust[u.id]).length], ["With projects", customers.filter((u) => projByCust[u.id]).length],
        ["No activity", customers.filter((u) => !reqByCust[u.id] && !projByCust[u.id]).length],
      ],
      breakdowns: [
        { title: "Area-wise (from requests)", items: groupTop(customers, (u) => reqByCust[u.id]?.[0]?.city) },
        { title: "Service-wise (from requests)", items: groupTop(requests, (r) => (Array.isArray(r.services) ? r.services[0] : r.category)) },
      ],
      table: {
        rows: customers,
        searchKeys: ["id", "name", "status"],
        filters: [{ key: "act", label: "Activity", options: [{ value: "req", label: "Has requests" }, { value: "proj", label: "Has projects" }, { value: "none", label: "No activity" }], predicate: (r, v) => v === "req" ? !!reqByCust[r.id] : v === "proj" ? !!projByCust[r.id] : !reqByCust[r.id] && !projByCust[r.id] }],
        columns: [
          { key: "name", label: "Customer", render: (u) => <div><p className="font-medium text-foreground">{text(u.name || u.full_name, null) || `Customer ${String(u.id).slice(0, 6)}`}</p><p className="text-[11px] font-mono text-muted-foreground">{u.id}</p></div>, sortValue: (u) => u.name || u.id },
          { key: "city", label: "City", render: (u) => custCity(u), sortValue: (u) => custCity(u) },
          { key: "reqs", label: "Requests", render: (u) => (reqByCust[u.id] || []).length, sortValue: (u) => (reqByCust[u.id] || []).length },
          { key: "projs", label: "Projects", render: (u) => (projByCust[u.id] || []).length, sortValue: (u) => (projByCust[u.id] || []).length },
          { key: "status", label: "Status", render: (u) => <StatusBadge status={u.status || "active"} /> },
          { key: "joined", label: "Joined", render: (u) => fmtDate(u.created_at), sortValue: (u) => toDate(u.created_at)?.getTime() || 0 },
        ],
      },
    },
    {
      key: "requests", label: "Requests / Leads", icon: "Send", accent: "indigo", value: String(requests.length),
      tiles: [
        ["Total", requests.length], ["Ops required", requests.filter((r) => r.internal_status === "ops_required").length],
        ["Ready", requests.filter((r) => r.internal_status === "ready").length], ["Hired", requests.filter((r) => r.customer_status === "hired").length],
        ["No responses", requests.filter((r) => r.customer_status === "no_responses").length], ["0 leads floated", zeroReads.length],
      ],
      breakdowns: [
        { title: "By city", items: groupTop(requests, (r) => r.city) },
        { title: "By service", items: groupTop(requests, (r) => (Array.isArray(r.services) ? r.services[0] : r.category)) },
      ],
      table: {
        rows: requests,
        searchKeys: ["id", "title", "city", "internal_status", "customer_status"],
        filters: [
          { key: "int", label: "Internal", options: [...new Set(requests.map((r) => r.internal_status).filter(Boolean))].map((v) => ({ value: v, label: v })), predicate: (r, v) => r.internal_status === v },
          { key: "cust", label: "Customer status", options: [...new Set(requests.map((r) => r.customer_status).filter(Boolean))].map((v) => ({ value: v, label: v })), predicate: (r, v) => r.customer_status === v },
        ],
        columns: [
          { key: "title", label: "Request", render: (r) => <div><p className="font-medium text-foreground max-w-[220px] truncate">{text(r.title || r.category)}</p><p className="text-[11px] font-mono text-muted-foreground">{r.id}{r.project_number ? ` · #${r.project_number}` : ""}</p></div>, sortValue: (r) => r.title },
          { key: "customer", label: "Customer", render: (r) => text(nameById[r.customer_id], null) || <span className="font-mono text-[11px]">{text(r.customer_id)}</span>, sortValue: (r) => r.customer_id },
          { key: "city", label: "City", render: (r) => text(r.city) },
          { key: "floated", label: "Floated", render: (r) => <span className={(leadCountByReq[r.id] || 0) === 0 ? "text-red-600 font-mono" : "font-mono"}>{leadCountByReq[r.id] || 0}</span>, sortValue: (r) => leadCountByReq[r.id] || 0 },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.internal_status || r.customer_status || "open"} /> },
          { key: "created", label: "Created", render: (r) => fmtDate(r.created_at), sortValue: (r) => toDate(r.created_at)?.getTime() || 0 },
        ],
      },
    },
    {
      key: "floated", label: "Floated Leads / Zero Reads", icon: "Radar", accent: "cyan", value: `${leads.length} / ${zeroReads.length}`,
      tiles: [
        ["Leads floated", leads.length], ["Responded", respondedLeads], ["Expired", expiredLeads],
        ["Requests w/ 0 leads", zeroReads.length], ["Active pros", activePros],
      ],
      breakdowns: [{ title: "Zero-read requests by city", items: groupTop(zeroReads, (r) => r.city) }],
      table: {
        rows: zeroReads,
        searchKeys: ["id", "title", "city", "internal_status"],
        filters: [],
        columns: [
          { key: "title", label: "Request (0 leads floated)", render: (r) => <div><p className="font-medium text-foreground max-w-[240px] truncate">{text(r.title || r.category)}</p><p className="text-[11px] font-mono text-muted-foreground">{r.id}</p></div>, sortValue: (r) => r.title },
          { key: "city", label: "City", render: (r) => text(r.city) },
          { key: "status", label: "Internal", render: (r) => <StatusBadge status={r.internal_status || "open"} /> },
          { key: "created", label: "Created", render: (r) => fmtDate(r.created_at), sortValue: (r) => toDate(r.created_at)?.getTime() || 0 },
        ],
      },
    },
    {
      key: "projects", label: "Active Projects", icon: "FolderKanban", accent: "emerald", value: String(activeProjects.length),
      tiles: [
        ["Total projects", projects.length], ["Active", activeProjects.length], ["Brugo Promise", promiseProjects.length],
        ["At risk / delayed", projects.filter((p) => ["at_risk", "delayed", "off_track"].includes(p.promise_status)).length], ["Completed", projects.filter((p) => p.status === "completed").length],
      ],
      breakdowns: [{ title: "By city", items: groupTop(projects, (p) => p.city) }],
      table: {
        rows: activeProjects,
        searchKeys: ["id", "title", "city", "project_number", "status"],
        filters: [
          { key: "st", label: "Status", options: [...new Set(projects.map((p) => p.status).filter(Boolean))].map((v) => ({ value: v, label: v })), predicate: (p, v) => p.status === v },
          { key: "pr", label: "Type", options: [{ value: "promise", label: "Brugo Promise" }, { value: "normal", label: "Normal" }], predicate: (p, v) => v === "promise" ? !!p.brugo_promise : !p.brugo_promise },
        ],
        columns: [
          { key: "title", label: "Project", render: (p) => <div><p className="font-medium text-foreground max-w-[200px] truncate">{text(p.title || p.category)}</p><p className="text-[11px] font-mono text-muted-foreground">{p.project_number ? `#${p.project_number}` : p.id}</p></div>, sortValue: (p) => p.title },
          { key: "customer", label: "Customer", render: (p) => text(nameById[p.customer_id], null) || <span className="font-mono text-[11px]">{text(p.customer_id)}</span> },
          { key: "pro", label: "Professional", render: (p) => text(proById[proIds(p)[0]]?.full_name, null) || <span className="font-mono text-[11px]">{text(proIds(p)[0])}</span> },
          { key: "value", label: "Value", render: (p) => inr(p.quotation_total || p.total_estimated_cost), sortValue: (p) => Number(p.quotation_total || p.total_estimated_cost || 0) },
          { key: "promise", label: "Promise", render: (p) => (p.brugo_promise ? <StatusBadge status={p.promise_status || "on_track"} /> : <span className="text-xs text-muted-foreground">Normal</span>) },
          { key: "status", label: "Status", render: (p) => <StatusBadge status={p.status || "active"} /> },
        ],
      },
    },
    {
      key: "professionals", label: "Professionals", icon: "Briefcase", accent: "amber", value: String(pros.length),
      tiles: [
        ["Total", pros.length], ["Active", activePros], ["Onboarding", pros.filter((p) => ["draft", "submitted", "correction", "correction_needed"].includes(p.onboarding_status)).length],
        ["Approval queue", submittedPros.length], ["Suspended", pros.filter((p) => ["suspended", "flagged"].includes(p.status)).length],
      ],
      breakdowns: [
        { title: "By category", items: groupTop(pros, (p) => p.category_name) },
        { title: "By city", items: groupTop(pros, (p) => p.city) },
      ],
      table: {
        rows: pros,
        searchKeys: ["id", "full_name", "business_name", "phone", "category_name", "city"],
        filters: [{ key: "st", label: "Status", options: [...new Set(pros.map((p) => p.status).filter(Boolean))].map((v) => ({ value: v, label: v })), predicate: (p, v) => p.status === v }],
        columns: [
          { key: "name", label: "Professional", render: (p) => <div><p className="font-medium text-foreground">{text(p.full_name || p.business_name, "Unnamed")}</p><p className="text-[11px] font-mono text-muted-foreground">{text(p.phone)}</p></div>, sortValue: (p) => p.full_name },
          { key: "cat", label: "Category", render: (p) => <div><p className="text-sm">{text(p.category_name)}</p><p className="text-[11px] text-muted-foreground">{list(p.subcategories, ", ", "")}</p></div>, sortValue: (p) => p.category_name },
          { key: "city", label: "Service area", render: (p) => list(p.service_cities?.length ? p.service_cities : p.cities, ", ", text(p.city)), sortValue: (p) => p.city },
          { key: "cluster", label: "Cluster", render: (p) => text(p.cluster) },
          { key: "status", label: "Status", render: (p) => <StatusBadge status={p.status} /> },
        ],
      },
    },
    {
      key: "approvals", label: "Approval Queue", icon: "UserCheck", accent: "indigo", value: String(submittedPros.length),
      tiles: [
        ["Submitted", submittedPros.length], ["Correction needed", pros.filter((p) => ["correction", "correction_needed"].includes(p.onboarding_status)).length],
        ["Approved (all time)", pros.filter((p) => p.onboarding_status === "approved").length], ["Field-created", pros.filter((p) => p.created_by_role === "field").length],
      ],
      breakdowns: [{ title: "Queue by category", items: groupTop(submittedPros, (p) => p.category_name) }],
      table: {
        rows: submittedPros,
        searchKeys: ["id", "full_name", "phone", "category_name", "city"],
        filters: [],
        columns: [
          { key: "name", label: "Professional", render: (p) => <div><p className="font-medium text-foreground">{text(p.full_name || p.business_name, "Unnamed")}</p><p className="text-[11px] font-mono text-muted-foreground">{p.id}</p></div>, sortValue: (p) => p.full_name },
          { key: "cat", label: "Category", render: (p) => text(p.category_name) },
          { key: "city", label: "City", render: (p) => text(p.city) },
          { key: "submitted", label: "Submitted", render: (p) => fmtDate(p.updated_at), sortValue: (p) => toDate(p.updated_at)?.getTime() || 0 },
        ],
      },
    },
    {
      key: "care", label: "Pro Promise / Care & Disputes", icon: "ShieldAlert", accent: "red", value: String(disputes.length),
      tiles: [
        ["Total disputes", disputes.length], ["Open", openDisputes], ["Resolved", disputes.filter((d) => d.status === "resolved").length],
        ["Promise projects", promiseProjects.length], ["Promise at risk", promiseProjects.filter((p) => ["at_risk", "delayed", "off_track"].includes(p.promise_status)).length],
      ],
      breakdowns: [],
      table: {
        rows: disputes,
        searchKeys: ["id", "reason", "title", "project_id", "status"],
        filters: [{ key: "st", label: "Status", options: [...new Set(disputes.map((d) => d.status || "open"))].map((v) => ({ value: v, label: v })), predicate: (d, v) => (d.status || "open") === v }],
        columns: [
          { key: "reason", label: "Dispute", render: (d) => <div><p className="font-medium text-foreground text-sm">{text(d.reason || d.title || d.id)}</p><p className="text-[11px] font-mono text-muted-foreground">{d.id}</p></div>, sortValue: (d) => d.reason },
          { key: "project", label: "Project", render: (d) => <span className="font-mono text-xs">{text(d.project_id)}</span> },
          { key: "parties", label: "Parties", render: (d) => <span className="text-xs text-muted-foreground font-mono">{text(d.customer_id)} / {text(d.professional_id)}</span> },
          { key: "status", label: "Status", render: (d) => <StatusBadge status={d.status || "open"} /> },
          { key: "opened", label: "Opened", render: (d) => fmtDate(d.created_at), sortValue: (d) => toDate(d.created_at)?.getTime() || 0 },
        ],
      },
    },
    {
      key: "payments", label: "Payments / Payouts", icon: "CreditCard", accent: "emerald", value: inr(paymentsTotal),
      tiles: [
        ["Payments", payments.length], ["Total value", inr(paymentsTotal)], ["Captured", payments.filter((p) => (p.status || "captured") === "captured").length],
        ["Not captured", payments.filter((p) => (p.status || "captured") !== "captured").length],
      ],
      breakdowns: [{ title: "By type", items: groupTop(payments, (p) => p.kind) }],
      table: {
        rows: payments,
        searchKeys: ["id", "kind", "provider", "status", "customer_id", "project_id"],
        filters: [{ key: "kind", label: "Type", options: [...new Set(payments.map((p) => p.kind).filter(Boolean))].map((v) => ({ value: v, label: v })), predicate: (p, v) => p.kind === v }],
        columns: [
          { key: "id", label: "Payment", render: (p) => <span className="font-mono text-xs">{p.id}</span> },
          { key: "kind", label: "Type", render: (p) => <span className="capitalize text-sm">{text(p.kind)}</span> },
          { key: "customer", label: "Customer", render: (p) => text(nameById[p.customer_id], null) || <span className="font-mono text-[11px]">{text(p.customer_id)}</span> },
          { key: "amount", label: "Amount", render: (p) => inr(p.amount), sortValue: (p) => Number(p.amount || 0) },
          { key: "provider", label: "Provider", render: (p) => <span className="capitalize text-sm">{text(p.provider)}</span> },
          { key: "status", label: "Status", render: (p) => <StatusBadge status={p.status || "captured"} /> },
          { key: "date", label: "Date", render: (p) => fmtDate(p.created_at), sortValue: (p) => toDate(p.created_at)?.getTime() || 0 },
        ],
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [users, requests, projects, pros, payments, leads, disputes]);

  const card = cards.find((c) => c.key === active) || null;
  const { unseenBySection } = useNotifications();
  const KPI_SECTION = { requests: "requests", floated: "requests", projects: "projects", professionals: "professionals", approvals: "professionals", care: "care" };
  const KPI_HOT = {
    requests: requests.some((r) => healthOf("requests", r)?.level === "critical"),
    projects: projects.some((p) => healthOf("projects", p)?.level === "critical"),
    approvals: pros.some((p) => healthOf("professionals", p)),
    care: openDisputes > 0,
  };

  return (
    <div>
      <PageHeader title="Control Centre" subtitle="Live operational health across the shared Brugo backend (brugo-core-97965). Every card is a real-time Firestore count — click any card to open the exact records behind it." />

      <AttentionBoard />

      <h2 className="font-heading font-semibold text-foreground mb-3">Live numbers</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <StatCard key={c.key} testId={`kpi-${c.key}`} index={i} label={c.label} value={c.value} icon={c.icon} accent={c.accent} onClick={() => setActive(c.key)}
            dot={(unseenBySection[KPI_SECTION[c.key]] || 0) > 0} critical={!!KPI_HOT[c.key]} />
        ))}
      </div>

      <Card className="p-5 mt-6" data-testid="dashboard-activity">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.Activity className="h-4 w-4 text-primary" /> Recent Activity & Changes</h3>
          <Button size="sm" variant="ghost" onClick={() => nav("/activity")} data-testid="view-all-activity">View all <Icons.ChevronRight className="h-4 w-4 ml-1" /></Button>
        </div>
        <ActivityList items={recent} compact />
      </Card>

      <Sheet open={!!card} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="kpi-drawer">
          {card && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="font-heading flex items-center gap-2">{(() => { const I = Icons[card.icon] || Icons.Circle; return <I className="h-5 w-5 text-primary" />; })()} {card.label}</SheetTitle>
                <SheetDescription>Live from Firestore · {card.table.rows.length} record(s) behind this number.</SheetDescription>
              </SheetHeader>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {card.tiles.map(([label, val]) => (
                  <div key={label} className="rounded-lg border border-border bg-secondary/50 p-3" data-testid={`kpi-tile-${label}`}>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
                    <p className="mt-1 font-heading text-xl font-bold text-foreground">{val}</p>
                  </div>
                ))}
              </div>

              {card.breakdowns.filter((b) => b.items.length).length > 0 && (
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {card.breakdowns.filter((b) => b.items.length).map((b) => (
                    <div key={b.title} className="rounded-lg border border-border p-3">
                      <p className="text-xs font-semibold text-foreground mb-2">{b.title}</p>
                      <div className="space-y-1">
                        {b.items.map(([k, n]) => <div key={k} className="flex items-center justify-between text-sm"><span className="text-muted-foreground truncate">{k}</span><span className="font-mono text-foreground">{n}</span></div>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6">
                <h4 className="font-heading font-semibold text-foreground mb-3 text-sm flex items-center gap-2"><Icons.Table2 className="h-4 w-4 text-primary" /> Records</h4>
                <RecordTable testId={`kpi-table-${card.key}`} path={KPI_PATH[card.key]} rows={card.table.rows} columns={card.table.columns} searchKeys={card.table.searchKeys} filters={card.table.filters} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
